import React from "react";
import cn from "classnames";
import { observer, Observer } from "mobx-react";
import moment from "moment";
import locale from "@bonliva-crm/locale";
import { Form } from "@modules/formx-react";
import { AccommodationSelector } from "@containers/VirtualSelector/AccommodatioSelector";
import { toSEK } from "@utils/formatCurrency";
import { useSnackbars, useUploadRequest } from "@hooks";
import { useNavigation } from "@navigation";
import { Overlay, PrimaryButton, RegularButton, SpinnerOverlay, Topbar } from "@common";
import { BookingWeekCardQuery } from "@bookings/BookingWeekCard/BookingWeekCardQuery";
import { Checkbox, Date, FormxDebugger, Select, Text } from "@common/formx";
import { FileList } from "@common/FileList/FileList";
import { WorkRequestPeriods } from "../../components/week-periods";
import { weeksToDurations } from "@bonliva-crm/common";
import { AccommodationConfirmationStore } from "./AccommodationConfirmationStore";
import { ErpTaxWeekLines } from "./erp-tax-week-lines";
import { getWeekPayFormTotals } from "./erp-week-pay-form-totals";
import {
  Button,
  Card,
  DialogActions,
  Divider,
  Grid,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from "@material-ui/core";
import { DnDLocalContainer } from "../../../containers/DnDContainer/DnDLocalContainer";
import { FileUpload } from "mdi-material-ui";
import { FormSection } from "@common/forms";
import { WithCopyToClipboard } from "@common/with-copy-to-clipboard";
import { ThemeType } from "@theme";
import { LegalPersonType } from "@bonliva-crm/graphql";
import { AccommodationConfirmationSource } from "@bonliva-crm/shared";
import { environment } from "../../../../environments/environment";

type Props = {
  isNew: boolean;
  formStore: AccommodationConfirmationStore;
};

export const AccommodationConfirmationForm = observer((props: Props) => {
  const { formStore } = props;
  const { showSuccess, showError } = useSnackbars();
  const { requestUpload } = useUploadRequest(true);
  const nav = useNavigation();
  const classes = useStyles({});

  const squareFootageAttachedError = formStore.form.getError("squareFootageAttached");

  const readOnly = formStore.readOnly;
  const isErp = formStore.form.values.source === AccommodationConfirmationSource.Erp;
  const erpTaxationLink = formStore.erpTaxationId
    ? `${environment.babErpUrl}/taxation/${formStore.erpTaxationId}`
    : null;
  const syncedAt = formStore.form.values.syncedAt;

  return (
    <Grid container spacing={2} style={{ padding: 16, marginTop: -16 }}>
      <Grid item xs={12}>
        <Topbar>
          <Typography variant="h6">Ladda upp bokningsbekräftelse</Typography>
        </Topbar>

        <Divider style={{ margin: "8px -16px 0" }} />
      </Grid>

      <Grid item xs={12}>
        <BookingWeekCardQuery bookingId={formStore.bookingId} />
      </Grid>

      <Grid item xs={12}>
        <Form store={formStore.form} allowUpdates>
          <FormxDebugger />

          <Paper>
            <div style={{ position: "relative" }}>
              <SpinnerOverlay
                show={formStore.form.isSubmitting}
                style={{ margin: "-16px", borderRadius: "4px 4px 0 0" }}
              />
            </div>

            <Grid container>
              <Grid item xs={12} style={{ padding: 16 }}>
                {/* ERP-sourced mirrors carry no file — hide the uploader. */}
                {!readOnly && (
                  <>
                    <FormSection
                      flat
                      fullWidth
                      withBorder
                      PaperProps={{ style: { padding: 0 } }}
                    >
                      <DnDLocalContainer
                        className={classes.withFileUpload}
                        overlayProps={{ className: classes.dndOverlay }}
                        onDrop={(files) => formStore.form.push("files", files)}
                        noClick
                      >
                        <RegularButton
                          variant="text"
                          leftIcon={<FileUpload />}
                          onClick={() =>
                            requestUpload().then((files) =>
                              formStore.form.push("files", files)
                            )
                          }
                          style={{ margin: "8px auto" }}
                        >
                          {"Ladda upp fil"}
                        </RegularButton>
                        <div style={{ width: "100%", padding: "0 16px" }}>
                          <Typography variant="subtitle2">
                            Filer att ladda upp:
                          </Typography>
                          <FileList
                            files={formStore.form.values.files}
                            onDelete={(i) => formStore.form.removeByIndex("files", i)}
                          />
                        </div>
                      </DnDLocalContainer>
                    </FormSection>

                    <Divider style={{ margin: "8px -16px" }} />
                  </>
                )}

                {!isErp && (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      Välj de veckor för vilka boendet gäller:
                    </Typography>

                    <div style={{ pointerEvents: readOnly ? "none" : undefined }}>
                      <WorkRequestPeriods
                        bookings={weeksToDurations(
                          formStore.bookingData.BookingGroup.bookings.map((x) => x.Week)
                        )}
                        disableNotBooked
                        skipDisabledSpans
                        // disabledWeeks={formStore.disabledWeeks}
                        readonly={readOnly}
                        onChange={readOnly ? null : (v) => formStore.setWeeks(v)}
                        value={formStore.weeksValue}
                      />
                    </div>

                    <Divider style={{ margin: "8px -16px" }} />
                  </>
                )}

                {!isErp && (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      Välj de veckor som du vill ta upp boendet på:
                    </Typography>

                    <div style={{ pointerEvents: readOnly ? "none" : undefined }}>
                      <WorkRequestPeriods
                        bookings={weeksToDurations(
                          formStore.bookingData.BookingGroup.bookings.map((x) => x.Week)
                        )}
                        disableNotBooked
                        skipDisabledSpans
                        // disabledWeeks={formStore.disabledWeeks}
                        readonly={readOnly}
                        disabled={!readOnly && formStore.taxPaymentWeekSelectorDisabled}
                        onChange={readOnly ? null : (v) => formStore.setTaxPaymentWeek(v)}
                        value={formStore.taxedWeekValue}
                      />
                    </div>
                  </>
                )}

                {isErp && (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      {locale.accommodationWeeksFromErp}
                    </Typography>

                    <div style={{ pointerEvents: "none" }}>
                      <WorkRequestPeriods
                        bookings={weeksToDurations(
                          formStore.bookingData.BookingGroup.bookings.map((x) => x.Week)
                        )}
                        disableNotBooked
                        skipDisabledSpans
                        readonly
                        value={formStore.taxedWeekValue}
                      />
                    </div>
                  </>
                )}

                {isErp && (
                  <>
                    <Paper className={classes.erpBanner} elevation={0}>
                      <div>
                        <Typography variant="body2">
                          {locale.accommodationManagedInErp}
                        </Typography>
                        {syncedAt && (
                          <Typography variant="caption" color="textSecondary">
                            {`${locale.accommodationSyncedAt}: ${moment(syncedAt).format(
                              "YYYY-MM-DD HH:mm"
                            )}`}
                          </Typography>
                        )}
                      </div>
                      {erpTaxationLink && (
                        <WithCopyToClipboard contentToCopy={erpTaxationLink}>
                          <a
                            href={erpTaxationLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.erpBannerLink}
                          >
                            {locale.openInErp}
                          </a>
                        </WithCopyToClipboard>
                      )}
                    </Paper>
                  </>
                )}

                <Divider style={{ margin: "8px -16px" }} />
              </Grid>

              {!isErp && (
                <Grid item xs={12} style={{ padding: 16 }}>
                  {formStore.isNew && (
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      Välj boende från boenderegistret:
                    </Typography>
                  )}

                  <AccommodationSelector
                    clearable
                    label={locale.Accommodation}
                    onChange={(value) => formStore.setAccommodation(value)}
                    value={{
                      accommodationId: formStore.form.values.accommodationId,
                      offerId: formStore.form.values.offerId
                    }}
                    error={formStore.form.getError((x) => x?.accommodationId)}
                    fullWidth
                    required={!readOnly}
                    disabled={!formStore.isNew || readOnly}
                  />

                  {formStore.isNew && formStore.form.values.accommodationId && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 8
                      }}
                    >
                      <Button variant="outlined" onClick={formStore.clearAccommodation}>
                        {locale.Clear}
                      </Button>
                    </div>
                  )}

                  {formStore.offerOptions.length > 0 && (
                    <Select
                      disabled={formStore.isNew}
                      name="offerId"
                      options={formStore.offerOptions}
                      label={locale.Rooms}
                    />
                  )}
                </Grid>
              )}

              {!isErp && (
                <Grid item xs={4} style={{ padding: 16 }}>
                  <div>
                    <Text
                      name="totalCost"
                      label="Verklig kostnad"
                      TextFieldProps={{
                        required: !readOnly,
                        type: "number",
                        style: { marginTop: 8 },
                        InputProps: {
                          endAdornment: "kr."
                        }
                      }}
                    />
                  </div>
                  {formStore.bookingData?.Consultant?.LegalPersonType ===
                    LegalPersonType.Subcontractor && (
                    <div>
                      <Checkbox
                        name={"totalExVAT"}
                        label={
                          <Typography
                            color={
                              formStore.form.getError("totalExVAT") ? "error" : "initial"
                            }
                          >
                            {locale.TotalExcludeVAT}?
                          </Typography>
                        }
                      />
                    </div>
                  )}

                  <div style={{ marginTop: 8, position: "relative" }}>
                    <Text
                      name="area"
                      label="Area (skriv 0 på underleverantör)"
                      TextFieldProps={{ type: "number", style: { marginTop: 8 } }}
                      disabled={!formStore?.isConsultantEmployee}
                    />
                    {formStore?.isConsultantEmployee && (
                      <Checkbox
                        name="squareFootageAttached"
                        CheckboxProps={{
                          className: cn(!!squareFootageAttachedError && classes.error)
                        }}
                        FormControlLabelProps={{ className: classes.squareFootageField }}
                        label={locale.squareFootageAttached}
                      />
                    )}
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <Select
                      disabled={formStore.cityInhabitantsLoading}
                      name="city"
                      label="Ort"
                      error={!!formStore.cityInhabitantsError}
                      multi={false}
                      options={formStore.cityInhabitants.map((x) => ({
                        label: x.city,
                        value: x.city,
                        description: inhabitantsToString(x.taxRegion, x.inhabitants)
                      }))}
                    />
                    {formStore.missingTaxError && (
                      <Typography
                        variant="caption"
                        color="error"
                        // style={{ position: "absolute", bottom: -16 }}
                      >
                        {formStore.missingTaxError}
                      </Typography>
                    )}
                  </div>
                </Grid>
              )}

              {/* ERP-573: the living area (kvm) + tax city the ERP taxation
                  carries, mirrored onto the confirmation root. Read-only — ERP
                  owns these values. */}
              {isErp && (
                <Grid item xs={4} style={{ padding: 16 }}>
                  <Text
                    name="area"
                    label={locale.accommodationArea}
                    TextFieldProps={{
                      type: "number",
                      style: { marginTop: 8 },
                      InputProps: { endAdornment: "kvm" }
                    }}
                    disabled
                  />
                  <div style={{ marginTop: 8 }}>
                    <Select
                      disabled
                      name="city"
                      label={locale.accommodationCity}
                      multi={false}
                      options={formStore.cityInhabitants.map((x) => ({
                        label: x.city,
                        value: x.city,
                        description: inhabitantsToString(x.taxRegion, x.inhabitants)
                      }))}
                    />
                  </div>
                </Grid>
              )}

              <Grid item xs={12} style={{ padding: "0 16px" }}>
                <Grid container spacing={2} style={{ paddingTop: 16 }}>
                  {formStore.form.values.weeks
                    .map((x, i) => ({ item: x, index: i }))
                    .filter((x) => x.item.isTaxed)
                    .sortBy((x) => x.item.weekYear.Week + x.item.weekYear.Year * 53)
                    .map(
                      ({
                        item: {
                          startDate,
                          endDate,
                          weekYear,
                          tax,
                          cost,
                          extraCost,
                          lines
                        },
                        index
                      }) => {
                        const payForm = getWeekPayFormTotals({
                          cost,
                          extraCost,
                          tax,
                          lines
                        });
                        return (
                          <Grid item xs={6} md={4} xl={3} key={index}>
                            <Card style={{ padding: 16, position: "relative" }}>
                              <Overlay in={formStore.isWeekDisabled(weekYear)} />

                              <Typography>
                                Vecka {weekYear.Week}-{weekYear.Year}
                              </Typography>

                              <Text
                                name={`weeks.${index}.cost`}
                                label="Kostnad"
                                TextFieldProps={{
                                  type: "number",
                                  InputProps: {
                                    endAdornment: <span>kr.</span>
                                  }
                                }}
                              />

                              <Text
                                name={`weeks.${index}.extraCost`}
                                label="Övriga kostnader kopplade till boendet"
                                TextFieldProps={{
                                  style: { marginTop: 8 },
                                  type: "number",
                                  InputProps: {
                                    endAdornment: <span>kr.</span>
                                  }
                                }}
                              />
                              {parseInt(
                                formStore.form.getValue(
                                  `weeks.${index}.extraCost`
                                ) as string
                              ) > 0 && (
                                <Text
                                  name={`weeks.${index}.extraDescription`}
                                  label="Beskrivning övriga kostnader kopplat till boende"
                                  TextFieldProps={{
                                    style: { marginTop: 8 }
                                  }}
                                />
                              )}

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between"
                                }}
                              >
                                <Date
                                  name={`weeks.${index}.startDate`}
                                  label={locale.FromAcc}
                                  DatePickerProps={{
                                    maxDate: endDate
                                      ? moment.utc(endDate).subtract(1, "day").toDate()
                                      : null
                                  }}
                                />
                                <Date
                                  name={`weeks.${index}.endDate`}
                                  label={locale.ToAcc}
                                  DatePickerProps={{
                                    minDate: startDate
                                      ? moment.utc(startDate).add(1, "day").toDate()
                                      : null
                                  }}
                                />
                              </div>

                              <Text
                                name={`weeks.${index}.nights`}
                                label="Nätter"
                                TextFieldProps={{
                                  style: { marginTop: 8 },
                                  type: "number"
                                }}
                              />

                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Typography>Total förmånsbeskattning:</Typography>
                                <Typography style={{ flex: 1, textAlign: "right" }}>
                                  {toSEK(payForm.tax)}
                                </Typography>
                              </div>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Typography>Total kostnad (ej skatt):</Typography>
                                <Typography style={{ flex: 1, textAlign: "right" }}>
                                  {toSEK(payForm.cost)}
                                </Typography>
                              </div>
                              {payForm.salaryDeduction > 0 && (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Typography>
                                    {locale.accommodationSalaryDeduction}:
                                  </Typography>
                                  <Typography style={{ flex: 1, textAlign: "right" }}>
                                    {toSEK(payForm.salaryDeduction)}
                                  </Typography>
                                </div>
                              )}
                              <Text
                                name={`weeks.${index}.note`}
                                label={locale.Notes}
                                TextFieldProps={{
                                  style: { marginTop: 8 }
                                }}
                              />
                              {isErp && lines?.length > 0 && (
                                <ErpTaxWeekLines lines={lines} />
                              )}
                            </Card>
                          </Grid>
                        );
                      }
                    )}
                </Grid>
              </Grid>

              <Grid item xs={8} container alignItems="flex-end" style={{ padding: 16 }}>
                <Button
                  variant="outlined"
                  onClick={formStore.distributeCosts}
                  disabled={readOnly}
                >
                  Fördela verkliga kostnaden baserat på nätter
                </Button>
              </Grid>

              <Grid item xs={4} style={{ paddingTop: 16 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Antal nätter:</TableCell>
                      <TableCell align="right">{formStore.totalNights}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>Total verklig kostnad:</TableCell>
                      <TableCell align="right">
                        {toSEK(formStore.form.values.totalCost)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{ borderBottom: 0 }}>
                        Total förmånsbeskattning:
                      </TableCell>
                      <TableCell align="right" style={{ borderBottom: 0 }}>
                        {toSEK(formStore.totalTax)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ borderBottom: 0 }}>
                        Summa övriga kostnader:
                      </TableCell>
                      <TableCell align="right" style={{ borderBottom: 0 }}>
                        {toSEK(formStore.totalExtraCost)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>
            </Grid>

            <Divider style={{ margin: "0 0 8px 0" }} />

            <Observer>
              {() => (
                <DialogActions style={{ padding: 16 }}>
                  <RegularButton
                    style={{ marginLeft: "auto" }}
                    onClick={() => nav.close()}
                  >
                    {readOnly ? locale.Close : locale.Cancel}
                  </RegularButton>

                  {/* ERP-owned mirrors are read-only — no Save affordance. */}
                  {!readOnly && (
                    <PrimaryButton
                      disabled={!formStore.form.canSubmit}
                      loading={formStore.form.isSubmitting}
                      onClick={() => {
                        formStore
                          .submit()
                          .then(() => {
                            showSuccess(locale.Saved);
                            nav.unBlock("accom_doc_uploader");
                            nav.close();
                          })
                          .catch((err) =>
                            showError(`Cannot save\nReason: ${err.toString()}`)
                          );
                      }}
                    >
                      {locale.Save}
                    </PrimaryButton>
                  )}
                </DialogActions>
              )}
            </Observer>
          </Paper>
        </Form>
      </Grid>
    </Grid>
  );
});

function inhabitantsToString(taxRegion: string, v: number) {
  if (taxRegion) {
    return taxRegion;
  }

  return `${v.toLocaleString(undefined, { minimumFractionDigits: 0 })} invånare`;
}

const useStyles = makeStyles((theme: ThemeType) => ({
  dndOverlay: { borderRadius: 4 },

  withFileUpload: {
    // borderStyle: "dashed",
    display: "flex",
    flex: 1,
    flexWrap: "wrap",
    justifyContent: "space-between",
    // padding: 16,
    paddingTop: 0,
    paddingBottom: 0
  },
  error: {
    color: theme.palette.error.main
  },
  squareFootageField: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    maxWidth: 250,
    marginLeft: 5
  },
  erpBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: "10px 16px",
    backgroundColor: "#fff4e5",
    border: "1px solid #ff9800",
    borderRadius: 4,
    marginTop: 16
  },
  erpBannerLink: {
    whiteSpace: "nowrap",
    fontWeight: 600,
    color: theme.palette.primary.main
  }
}));
